/**
 * UserActivityWidget — analytics-v2 user activity trend (CORE-1206).
 *
 * Engineering-lane widget — receives `environment` prop from the dashboard's
 * global env selector. DB data is env-scoped (dev has test users, prod has
 * real users), making the env selector genuinely meaningful.
 *
 * Shows:
 *   - Summary stat row: total registrations, avg DAU, active buyers, repeat-buyer rate
 *   - Two stacked line charts sharing the same X-axis window: daily new
 *     registrations (top) and daily distinct-users-who-logged-in (bottom)
 *   - Attribution caption + last updated timestamp
 *
 * Null handling:
 *   - `avgDau === null` → "—" (no daily data to average)
 *   - `repeatBuyerRate === null` → "—" (activeBuyers = 0; no meaningful ratio)
 */

import { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getAnalyticsUserActivity } from '@/api/client';
import {
  formatCount,
  formatDateTime,
  formatRatioAsPercent,
} from '@/utils/format';
import { shortDate } from '@/utils/shortDate';
import { useAnalyticsV2Fetch } from '@/hooks/useAnalyticsV2Fetch';
import { attributionCaption } from '@/utils/attribution';
import { AnalyticsV2ErrorBoundary } from './AnalyticsV2ErrorBoundary';
import type {
  Environment,
  FunnelWindow,
  LoginDayRow,
  RegistrationDayRow,
} from '@/types/api';

const WINDOW_OPTIONS: readonly { value: FunnelWindow; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

interface UserActivityWidgetProps {
  environment: Environment;
}

export function UserActivityWidget({
  environment,
}: UserActivityWidgetProps): JSX.Element {
  const [selectedWindow, setSelectedWindow] = useState<FunnelWindow>('30d');

  return (
    <AnalyticsV2ErrorBoundary
      title="User Activity"
      resetKey={`${environment}-${selectedWindow}`}
    >
      <UserActivityWidgetBody
        environment={environment}
        selectedWindow={selectedWindow}
        onWindowChange={setSelectedWindow}
      />
    </AnalyticsV2ErrorBoundary>
  );
}

interface UserActivityWidgetBodyProps {
  environment: Environment;
  selectedWindow: FunnelWindow;
  onWindowChange: (w: FunnelWindow) => void;
}

function UserActivityWidgetBody({
  environment,
  selectedWindow,
  onWindowChange,
}: UserActivityWidgetBodyProps): JSX.Element {
  const { data, error, isLoading } = useAnalyticsV2Fetch(
    () =>
      getAnalyticsUserActivity({ env: environment, window: selectedWindow }),
    [environment, selectedWindow],
  );

  return (
    <section className="user-activity-widget">
      <header className="user-activity-widget-header">
        <h3>
          User Activity
          {/*
            aria-hidden on the badge — the accessible-name algorithm
            concatenates all descendant text, so without this screen readers
            would announce "User Activity dev". The sr-only span below
            carries the env announcement as real text content.
          */}
          <span className="analytics-v2-env-badge" aria-hidden="true">
            {environment}
          </span>
        </h3>
        <span className="sr-only">Environment: {environment}</span>
      </header>

      <div className="analytics-v2-widget-controls">
        <label>
          Window
          <select
            className="analytics-v2-widget-select"
            value={selectedWindow}
            onChange={(e) => onWindowChange(e.target.value as FunnelWindow)}
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading && (
        <p className="user-activity-widget-loading">
          Loading user activity data…
        </p>
      )}

      {!isLoading && error !== null && (
        <div className="user-activity-widget-error" role="alert">
          Could not load user activity data: {error}
        </div>
      )}

      {!isLoading && error === null && data !== null && (
        <>
          <p className="user-activity-widget-caption">
            Attribution: {attributionCaption(data.attributionWindow)}. Last
            updated {formatDateTime(data.lastUpdated)}.
          </p>

          <div className="analytics-stat-grid">
            <div className="analytics-stat">
              <span className="analytics-stat-value">
                {formatCount(data.totalRegistrations)}
              </span>
              <span className="analytics-stat-label">Total registrations</span>
            </div>
            <div className="analytics-stat">
              <span className="analytics-stat-value">
                {formatCount(data.avgDau)}
              </span>
              <span className="analytics-stat-label">Avg DAU</span>
            </div>
            <div className="analytics-stat">
              <span className="analytics-stat-value">
                {formatCount(data.activeBuyers)}
              </span>
              <span className="analytics-stat-label">Active buyers</span>
            </div>
            <div className="analytics-stat">
              <span className="analytics-stat-value">
                {formatRatioAsPercent(data.repeatBuyerRate)}
              </span>
              <span className="analytics-stat-label">Repeat-buyer rate</span>
            </div>
          </div>

          <StackedLineChart
            title="Daily new registrations"
            dataKey={'count' satisfies keyof RegistrationDayRow}
            stroke="#3b82f6"
            rows={data.dailyRegistrations}
          />
          <StackedLineChart
            title="Daily active users (DAU)"
            dataKey={'distinctUsers' satisfies keyof LoginDayRow}
            stroke="#10b981"
            rows={data.dailyLogins}
          />
        </>
      )}
    </section>
  );
}

/**
 * One of the two stacked line charts in this widget. Both share the same
 * X-axis window (backfilled by the API), so scanning vertically across a
 * given date on both charts is meaningful. Kept as two separate charts
 * (instead of dual-axis on one chart) because scale mismatch on shared
 * axes hides relative-change asymmetry — see CORE-1206 design note.
 *
 * `isAnimationActive={false}` — dashboard prefers instant render over
 * reveal animation, so a manual refresh doesn't visibly re-draw both
 * charts every time.
 */
function StackedLineChart<T extends { date: string }>({
  title,
  dataKey,
  stroke,
  rows,
}: {
  title: string;
  dataKey: keyof T;
  stroke: string;
  rows: readonly T[];
}): JSX.Element {
  return (
    <div className="user-activity-widget-chart">
      <h4>{title}</h4>
      <ResponsiveContainer width="100%" height={180}>
        {/* Array.from(rows) strips the `readonly` and hands recharts a plain
            array. Recharts 3.10.1's source doesn't mutate props.data (verified
            via `.sort/.push/.splice` grep during CORE-1206 R1), but a future
            major upgrade could regress — the copy keeps the widget correct
            against that class of change. Same runtime cost as the old `[...]`
            spread; different justification. */}
        <LineChart data={Array.from(rows)} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tickFormatter={shortDate} />
          <YAxis allowDecimals={false} />
          <Tooltip labelFormatter={(d) => (typeof d === 'string' || typeof d === 'number' ? shortDate(String(d)) : '')} />
          <Line
            type="monotone"
            dataKey={dataKey as string}
            stroke={stroke}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
